"use client";
import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";

import type { TaxPlan } from "@/interfaces/dashboard";
import {
	Bar,
	BarChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Legend,
	ResponsiveContainer,
} from "recharts";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";

export function Obligation({ data }: { data: TaxPlan[] }) {
	const chartConfig = {
		totalTaxes: {
			label: "ພັນທະອາກອນ",
			color: "#344BFD",
		},
		plannedDividend: {
			label: "ເງິນປັນຜົນ",
			color: "#FF9359",
		},
	} satisfies ChartConfig;
	const processedData = data.map((item) => ({
		...item,
		totalTaxes: item.totalTaxes || 1, // Replace 0 with 1 for log scale
		plannedDividend: item.plannedDividend || 1,
	}));
	const formatYAxis = (value: number) => {
		if (value === 0) return "0";
		if (value >= 1000000000000)
			return `${(value / 1000000000000).toFixed(0)} ໂກດ`;
		if (value >= 1000000000) return `${(value / 1000000000).toFixed(0)} ຕື້`;
		if (value >= 1000000) return `${(value / 1000000).toFixed(0)} ລ້ານ`;
		if (value >= 100000) return `${(value / 100000).toFixed(0)} ແສນ`;
		if (value >= 1000) return `${(value / 1000).toFixed(0)} ພັນ`;
		if (value <= -1000000000000)
			return `-${(value / 1000000000000).toFixed(0)} ໂກດ`;
		if (value <= -1000000000) return `-${(value / 1000000000).toFixed(0)} ຕື້`;
		if (value <= -1000000) return `-${(value / 1000000).toFixed(0)} ລ້ານ`;
		if (value <= -100000) return `-${(value / 100000).toFixed(0)} ແສນ`;
		if (value <= -1000) return `-${(value / 1000).toFixed(0)} ພັນ`;
		if (value >= -100 && value < 0) return `-${(value).toFixed(0)}`;
		return value.toFixed(0);
	};

	const formatTooltipValue = (value: number | null) => {
		if (value === null) return "N/A";
		return new Intl.NumberFormat("la-LAOS", {
			style: "currency",
			currency: "LAK",
		}).format(value);
	};

	const [chartReady, setChartReady] = useState(false);

	useEffect(() => {
		setChartReady(true);
	}, []);
	const getLabelForKey = (key: string) => {
		return chartConfig[key as keyof typeof chartConfig]?.label || key;
	};
	return (
		<Card>
			<CardHeader>
				<CardTitle>ແຜນມອບພັນທະໃຫ້ລັດລວມທັງໝົດ</CardTitle>
				<CardDescription>ລາຍລະອຽດລາຍງານແຜນມອບພັນທະໃຫ້ລັດ</CardDescription>
			</CardHeader>
			<CardContent className="pt-4">
				{chartReady && (
					<ChartContainer config={chartConfig} className="h-[500px] w-full">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={processedData}
								margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="yearly" />
								<YAxis
									tickFormatter={formatYAxis}
									scale="log"
									domain={[1, "auto"]}
								/>
								<Legend />
								<ChartTooltip
									content={({ active, payload, label }) => {
										if (active && payload && payload.length) {
											return (
												<div className="p-2 border rounded-lg shadow-lg bg-background border-border">
													<p className="font-bold">{`Year: ${label}`}</p>
													{payload.map((entry, index) => (
														<p
															key={`item-${index}`}
															style={{ color: entry.color }}
														>
															{`${getLabelForKey(
																entry.dataKey as string,
															)}: ${formatTooltipValue(entry.value as number)}`}
														</p>
													))}
												</div>
											);
										}
										return null;
									}}
								/>
								{Object.keys(chartConfig).map((key) => (
									<Bar
										key={key}
										dataKey={key}
										name={getLabelForKey(key)}
										fill={chartConfig[key as keyof typeof chartConfig].color}
										radius={[4, 4, 0, 0]}
										maxBarSize={40}
									/>
								))}
							</BarChart>
						</ResponsiveContainer>
					</ChartContainer>
				)}
			</CardContent>
		</Card>
	);
}
